// /couples — household use-case landing page (couples + roommates).
// Grounded in the shipped household mode: personal-unless-recognized triage,
// merchant-rule learning, recurring bills, monthly settle. Claim hygiene:
// no named-competitor claims on this page — it sells our own workflow.
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Home, Repeat, Brain, ShieldCheck, ArrowRight } from 'lucide-react'
import PublicShell from '../components/PublicShell'

const FAQ = [
  {
    q: 'How do couples split expenses without typing everything in?',
    a: 'Upload your card statement to an AutoSplit household each month. Groceries, utilities, and subscriptions are suggested as shared; everything else starts as personal. You confirm the suggestions and promote anything else that was shared — the app remembers your answers for next month.',
  },
  {
    q: "Will my partner see my personal spending?",
    a: 'The default is personal-unless-recognized: unknown charges start excluded from the shared ledger. Nothing is billed to your partner until you confirm it.',
  },
  {
    q: 'How does AutoSplit handle rent and recurring bills?',
    a: 'Set up rent, utilities, or streaming once and they auto-generate every month. AutoSplit even spots charges that look recurring — same merchant, steady amount, multiple months — and offers to turn them into a bill with one tap.',
  },
  {
    q: 'Can we split a grocery receipt where some items are mine and some are shared?',
    a: 'Yes — itemize any expense and tag each line as mine, yours, or shared. The wine is yours, the protein powder is theirs, the $50 of groceries is 50/50.',
  },
]

const LOOP = [
  {
    icon: Home,
    title: 'Personal unless recognized',
    body: "Your statement is mostly your life, with shared expenses sprinkled in. So unknown charges start as personal — your partner is never accidentally billed for your haircut. Groceries, utilities, and subscriptions arrive as one-tap suggestions.",
  },
  {
    icon: Brain,
    title: 'It learns your merchants',
    body: 'Mark Safeway as 50/50 once. Next month\'s statement arrives with Safeway already assigned and confirmed. Every decision you make is one you never make again.',
  },
  {
    icon: Repeat,
    title: 'Bills run themselves',
    body: 'Rent, internet, streaming — set them once, they generate monthly. AutoSplit also spots repeat charges on your statements and suggests making them bills.',
  },
]

export default function CouplesPage() {
  useEffect(() => {
    const id = 'couples-faq-jsonld'
    let el = document.getElementById(id)
    if (!el) {
      el = document.createElement('script')
      el.id = id
      el.type = 'application/ld+json'
      document.head.appendChild(el)
    }
    el.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question', name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    })
    return () => document.getElementById(id)?.remove()
  }, [])

  return (
    <PublicShell
      title="Split Expenses With Your Partner or Roommates — AutoSplit Households"
      description="A shared ledger for couples and roommates that reads your card statement: shared expenses suggested, personal ones kept private, recurring bills automated. It learns your merchants so month two is nearly zero-touch."
    >
      <main className="relative z-10 max-w-5xl mx-auto px-6 pt-8 pb-16">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-4 leading-tight">
            The house ledger that<br />does its own bookkeeping
          </h1>
          <p className="text-ink-300 text-lg leading-relaxed mb-7">
            Living together means a hundred shared charges a month buried in
            personal statements. AutoSplit reads the statement, suggests what's
            shared, keeps what's yours private — and remembers every answer,
            so next month it mostly handles itself.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-lime-400 text-ink-950 font-bold hover:bg-lime-500 transition-colors shadow-lg shadow-lime-400/20"
          >
            Start your household <ArrowRight size={16} />
          </Link>
          <p className="text-xs text-ink-500 mt-3">Free while in beta · works for couples and roommates alike</p>
        </div>

        {/* ── The loop ─────────────────────────────────────────────────── */}
        <div className="grid md:grid-cols-3 gap-5 mb-14">
          {LOOP.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-ink-700 bg-ink-900/70 p-6">
              <div className="w-10 h-10 rounded-xl bg-lime-400/10 border border-lime-400/25 flex items-center justify-center mb-4">
                <Icon size={18} className="text-lime-400" />
              </div>
              <h2 className="font-display text-lg font-semibold mb-2">{title}</h2>
              <p className="text-sm text-ink-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* ── Privacy note — matters double for couples ────────────────── */}
        <div className="max-w-3xl mx-auto rounded-2xl border border-ink-700 bg-ink-900/50 p-7 mb-14 flex items-start gap-4">
          <ShieldCheck size={22} className="text-lime-400 flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-display text-lg font-semibold mb-1.5">Shared ledger, private lives</h2>
            <p className="text-sm text-ink-300 leading-relaxed">
              No bank linking — you upload the statement file you choose. And
              because unknown charges default to personal, the shared ledger
              only ever contains what you've confirmed as shared. Splitting
              the rent shouldn't mean sharing every purchase.
            </p>
          </div>
        </div>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-2xl font-semibold mb-6">Questions couples ask</h2>
          <div className="space-y-5">
            {FAQ.map(({ q, a }) => (
              <div key={q}>
                <h3 className="text-sm font-semibold text-ink-100 mb-1">{q}</h3>
                <p className="text-sm text-ink-400 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </PublicShell>
  )
}
